<template>
    <div :class="[textPlacementClass, cellAlignmentClass]" class="d-flex align-items-center h-100 w-100 px-1">
        <InlineImage
            v-if="inlineImageId"
            :ref="inlineImageId"
            :key="inlineImageId"
            :image-id="inlineImageId"
            :size="imageSize"
            :max-size="imageMaxSize"
            :style="imageStyle"
            :class="{ 'mx-1': hasText }"
        />
        <div v-if="hasText" :id="`cell-text-${index}`" :style="textStyle" class="cell-text" v-html="text"></div>
    </div>
</template>

<script lang="ts">
import { defineComponent, StyleValue } from 'vue'
import { mapGetters } from 'vuex'
import { stripHtmlTags } from '../../helpers/stringUtils'
import InlineImage from '../widgets/InlineImage.vue'

export default defineComponent({
    name: 'TableCellItemPreview',
    components: {
        InlineImage,
    },
    props: {
        item: {
            type: Object,
            default: undefined,
        },
        index: {
            type: Number,
            default: undefined,
        },
    },
    data() {
        return {
            imageMaxSize: 2 * 96, //2in in pixels
        }
    },
    computed: {
        ...mapGetters({
            style: 'document/documentStyle',
        }),
        text(): string {
            return this.item.term || ''
        },
        inlineImageId(): string | null {
            return this.item?.term_image
        },
        textPlacementClass(): string {
            return !this.hasImage || this.item?.style?.alignment !== 'center'
                ? ''
                : this.item?.style?.text_placement === 'below'
                  ? 'flex-column'
                  : 'flex-column-reverse'
        },
        cellAlignmentClass(): string {
            return this.item?.style.alignment === 'center'
                ? 'justify-content-center'
                : this.item?.style?.alignment === 'right'
                  ? 'justify-content-end'
                  : 'justify-content-left'
        },
        imageStyle(): StyleValue {
            let styles: StyleValue = {}
            styles.order =
                this.item?.style.alignment === 'center'
                    ? 0
                    : this.hasImage && this.item?.style?.image_placement === 'left'
                      ? 0
                      : 1

            return styles
        },
        textStyle(): StyleValue {
            let styles: StyleValue = {}
            styles.order =
                this.item?.style.alignment === 'center'
                    ? 0
                    : this.hasImage && this.item?.style?.image_placement === 'left'
                      ? 1
                      : 0
            styles.textAlign = this.item?.style.alignment !== 'center' ? 'left' : 'center'
            styles.fontSize = this.hasText ? `${this.style.font_size}px` : '14px'

            return styles
        },
        hasText(): boolean {
            return !!this.text && this.plainText.length > 0
        },
        hasImage(): boolean {
            return !!this.inlineImageId && this.inlineImageId.length > 0
        },
        imageSize(): number {
            if (!this.hasImage) return 0
            let itemHeight = this.element?.height || 0
            let itemWidth = this.element?.width || 0
            itemHeight = itemHeight > this.item.style.height && this.hasText ? itemHeight : this.item.style.height * 96
            itemWidth = itemWidth > this.item.style.width && this.hasText ? itemWidth : this.item.style.width * 96
            const imageSize = itemHeight > itemWidth ? itemWidth : itemHeight
            return this.item?.style?.alignment === 'center' && this.hasText ? itemHeight - this.textHeight : imageSize
        },
        textElement(): HTMLElement | null {
            const cellText = document.getElementById(`cell-text-${this.index}`)
            return cellText || null
        },
        textHeight(): number {
            return this.textElement?.getBoundingClientRect()?.height || 0
        },
        textWidth() {
            return this.textElement?.getBoundingClientRect()?.width || 0
        },
        element(): DOMRect | null {
            const id = this.index >= 0 ? `cell-index-${this.index}` : null
            if (id) {
                const el = document.getElementById(id)
                return el?.getBoundingClientRect() || null
            }
            return null
        },
        plainText(): string {
            return stripHtmlTags(this.text)
        },
    },
})
</script>
